import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useDispatch, useSelector } from 'react-redux';
import { unstagePupCard } from '../../redux/pupApi';
import { selectPupCatalog } from '../../redux/pupCatalogSlice';
import { playCardSnap } from '../../utilities/sounds';
import PupCard from './PupCard';
import './PupStagingBox.css';

// Movement (px) before a press counts as a drag rather than a click.
const DRAG_THRESHOLD_PX = 6;

// How long the sealing animation runs: the box's fiery glow, and the burn-away of the cards the
// set just swallowed. The cards are already gone from the authoritative list by then — these are
// decorative tiles held back purely so the set visibly closes instead of blinking out.
const FLASH_MS = 1000;
const BURN_MS = 720;

// Golds always trade three-for-one; silvers trade three-for-one unless Good economy's discount is
// live. Mirrors PupHandCodec.silverExchangeRate / GOLD_EXCHANGE_RATE — display only, since which
// copies are actually committed is decided server-side and arrives on the wire.
const GOLD_RATE = 3;

// A card's tier for the two-currency split. Prefers the catalog's authoritative `tier` (from
// PupCardMetadataTable) and falls back to the display colour; an unknown code — catalog still
// loading — reads as silver, matching the server's own fallback. It can never complete a gold set
// it isn't, because the server, not this, decides what counts.
const tierOf = (card) => (card?.tier || card?.color || 'SILVER').toUpperCase();

// Multiset difference: which codes are in `next` that weren't in `prev` (duplicates respected).
const addedCodes = (next, prev) => {
  const remaining = new Map();
  prev.forEach((code) => remaining.set(code, (remaining.get(code) || 0) + 1));
  const added = [];
  next.forEach((code) => {
    const left = remaining.get(code) || 0;
    if (left > 0) remaining.set(code, left - 1);
    else added.push(code);
  });
  return added;
};

// The exchange STAGING BOX — a drop zone docked beside the pup hand. Drag SILVER or GOLD pup
// cards from the dock (PupHand) into here to QUEUE them: when the Exchange stage fires at the top
// of the next round, every complete set of 3 staged silvers becomes 1 gold card and every set of
// 3 staged golds becomes 1 CELESTIAL (server-side; the two currencies swap independently).
// Celestials are the top tier and can never be staged — there is nothing above them to trade for.
//
// A set SELLS the moment it is complete: the server marks those exact copies committed
// (stagedCommitted, positionally aligned with the staged list) and refuses to unstage them, so the
// box seals them with a fiery flash and stops rendering them entirely. That is deliberate — a card
// nobody can drag back out should not sit there inviting the attempt. Only the sub-set remainder
// stays draggable, back over the dock, until it too completes a set.
//
// Staging state is private and authoritative server-side (state.pup.staged[roomId], a list of card
// CODES); the box is a mirror. Rendered inside the same .game-table as PupHand so `closest`
// resolves the right table in split-screen. The container itself is pointer-events: none (its rect
// is still the drop target PupHand hit-tests); only the free staged cards catch the pointer.
const PupStagingBox = ({ roomId, stage }) => {
  const dispatch = useDispatch();
  const stagedCodes = useSelector((state) => state.pup.staged[roomId]) || [];
  const committedFlags = useSelector((state) => (state.pup.stagedCommitted || {})[roomId]) || [];
  const goodEconomy = useSelector((state) => !!state.pup.exchangeRateTwo[roomId]);
  const catalog = useSelector(selectPupCatalog);
  // Live drag of a free staged card back toward the dock: { idx, code, x, y, startX, startY, active }.
  const [drag, setDrag] = useState(null);
  const [busyIdx, setBusyIdx] = useState(null);
  const [error, setError] = useState(null);
  // The sealing animation: the box's glow, plus the codes burning away with it. `seal` COUNTS
  // seals rather than flagging one, so it can key the flare element — a second set completed
  // while the first flare is still running remounts it and genuinely replays the animation,
  // where re-applying a class the element already carries would not.
  const [seal, setSeal] = useState(0);
  const [flashing, setFlashing] = useState(false);
  const [burning, setBurning] = useState([]);
  const rootRef = useRef(null);

  // While the Exchange stage is actively resolving the swap, the box is frozen (mirrors the
  // server, which rejects stage/unstage during currentStage === 'exchange').
  const locked = stage === 'exchange';

  const silverRate = goodEconomy ? 2 : GOLD_RATE;

  // Split the staged pile into what the player can still take back and what is already sold.
  // Committedness comes from the server — the client never decides it, so what the box hides is
  // exactly what unstageCard would refuse.
  const entries = stagedCodes.map((code, idx) => ({
    code,
    idx,
    tier: tierOf(catalog[code]),
    committed: !!committedFlags[idx],
  }));
  const free = entries.filter((e) => !e.committed);
  const sold = entries.filter((e) => e.committed);
  const freeSilver = free.filter((e) => e.tier !== 'GOLD').length;
  const freeGold = free.length - freeSilver;
  const soldSilverSets = Math.floor(sold.filter((e) => e.tier !== 'GOLD').length / silverRate);
  const soldGoldSets = Math.floor(sold.filter((e) => e.tier === 'GOLD').length / GOLD_RATE);
  const soldSets = soldSilverSets + soldGoldSets;

  // Fire the seal when the committed pile GROWS. Keyed on the committed codes themselves rather
  // than a count so a swap that both consumes and re-commits can't be missed; the first pass just
  // records a baseline, so hydrating an already-sealed box on reconnect never flashes.
  const committedCodes = sold.map((e) => e.code);
  const committedKey = committedCodes.join(',');
  const prevCommittedRef = useRef(null);
  useEffect(() => {
    const prev = prevCommittedRef.current;
    prevCommittedRef.current = committedCodes;
    if (prev === null) return undefined; // first render — nothing "just" completed
    const added = addedCodes(committedCodes, prev);
    if (!added.length) return undefined;
    setSeal((n) => n + 1);
    setFlashing(true);
    setBurning(added);
    const burnTimer = setTimeout(() => setBurning([]), BURN_MS);
    const flashTimer = setTimeout(() => setFlashing(false), FLASH_MS);
    return () => {
      clearTimeout(burnTimer);
      clearTimeout(flashTimer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [committedKey]);

  const isOverDock = (x, y) => {
    const dock = rootRef.current?.closest('.game-table')?.querySelector('.pup-hand');
    if (!dock) return false;
    const r = dock.getBoundingClientRect();
    return x >= r.left && x <= r.right && y >= r.top && y <= r.bottom;
  };

  const handleUnstage = async (code, idx) => {
    setBusyIdx(idx);
    setError(null);
    try {
      await dispatch(unstagePupCard({ roomId, card: code })).unwrap();
      playCardSnap();
    } catch (e) {
      setError(typeof e === 'string' && e ? e : 'Could not remove that card');
      setTimeout(() => setError(null), 4000);
    } finally {
      setBusyIdx(null);
    }
  };

  const onPointerDown = (e, entry) => {
    if (locked || busyIdx != null) return;
    e.currentTarget.setPointerCapture(e.pointerId);
    // Capture the CODE, not just the position: a private push can reshuffle the list mid-drag.
    setDrag({
      idx: entry.idx, code: entry.code,
      x: e.clientX, y: e.clientY, startX: e.clientX, startY: e.clientY, active: false,
    });
  };
  const onPointerMove = (e) => {
    if (!drag) return;
    const { clientX: x, clientY: y } = e;
    setDrag((d) => d && {
      ...d, x, y,
      active: d.active || Math.hypot(x - d.startX, y - d.startY) > DRAG_THRESHOLD_PX,
    });
  };
  const onPointerUp = (e) => {
    const d = drag;
    setDrag(null);
    if (!d || !d.active) return;
    if (isOverDock(e.clientX, e.clientY)) handleUnstage(d.code, d.idx);
    // Released elsewhere: keep it staged (the ghost just unmounts).
  };

  const overDock = drag?.active ? isOverDock(drag.x, drag.y) : false;
  const showCards = free.length > 0 || burning.length > 0;

  return (
    <div className={`pup-staging-box${locked ? ' pup-staging-box-locked' : ''}`} ref={rootRef}>
      {/* Keyed on the seal count so each completed set remounts it and genuinely replays. */}
      {flashing && <div className="pup-staging-flare" key={seal} />}
      {error && <div className="pup-hand-error pup-staging-error">{error}</div>}
      <div className="pup-staging-title">
        <span>Exchange box</span>
        <span className="pup-staging-count">
          {`⭐ ${freeSilver}/${silverRate}${soldSilverSets > 0 ? ` +${soldSilverSets}` : ''}`}
          {' · '}
          {`💎 ${freeGold}/${GOLD_RATE}${soldGoldSets > 0 ? ` +${soldGoldSets}` : ''}`}
        </span>
      </div>
      {!showCards ? (
        soldSets > 0 ? (
          // Nothing left to drag, but the box isn't empty — say so, or the vanished cards read
          // as a bug rather than as a completed trade.
          <div className="pup-staging-empty pup-staging-sealed">
            {`🔥 ${soldSets} set${soldSets > 1 ? 's' : ''} locked in`}
            <br />Trades at the next exchange
          </div>
        ) : (
          <div className="pup-staging-empty">
            {`Drag ${silverRate} silvers → gold`}<br />{`${GOLD_RATE} golds → celestial`}
          </div>
        )
      ) : (
        <div className="pup-staging-cards">
          {free.map((entry) => (
            <div
              key={`${entry.code}-${entry.idx}`}
              className={
                'pup-staging-card'
                + (busyIdx === entry.idx ? ' pup-staging-card-busy' : '')
                + (drag?.active && drag.idx === entry.idx ? ' pup-staging-card-dragged' : '')
              }
              onPointerDown={(e) => onPointerDown(e, entry)}
              onPointerMove={onPointerMove}
              onPointerUp={onPointerUp}
              onPointerCancel={() => setDrag(null)}
            >
              <div className="pup-staging-scale">
                <PupCard code={entry.code} roomId={roomId} />
              </div>
            </div>
          ))}
          {/* The cards the completed set just took. Already gone from the staged list and
              non-interactive — they only hang around long enough to burn away. */}
          {burning.map((code, i) => (
            <div key={`burn-${code}-${i}`} className="pup-staging-card pup-staging-card-burning">
              <div className="pup-staging-scale">
                <PupCard code={code} roomId={roomId} />
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Drag ghost riding the pointer while pulling a card back toward the dock. Reuses the
          dock's .pup-drag-ghost styling (PupHand.css is bundled globally). */}
      {drag?.active && drag.code && createPortal(
        <div
          className={`pup-drag-ghost${overDock ? ' pup-drag-ghost-over' : ''}`}
          style={{ left: drag.x, top: drag.y }}
        >
          <PupCard code={drag.code} roomId={roomId} />
          <div className="pup-drag-label">{overDock ? 'Release to keep' : 'Drag to your hand'}</div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default PupStagingBox;
